import {
  FocusContext,
  getCurrentFocusKey,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import { getLocalizedStations } from '../../actions/contentActions'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import Spinner from '../../components/Spinner'
import { LocalizationAuthStatus } from '../../components/VerifyLocation/types'
import { useHybridFocusable } from '../../hooks/useHybridFocusable/index'
import tts from '../../lib/TTS.js'
import { loadStationSchedules } from '../../lib/utils/station/stationUtils'
import { store } from '../../store.js'
import type { Station } from '../Livestream/types.js'

const COMPONENT_FOCUS_KEY = 'LOCALIZATION_ERROR'
const STATION_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_STATION_BUTTON`
const STATION_BUTTON_FOCUS_PREFIX = `${STATION_BUTTON_FOCUS_KEY}_`
const LIVESTREAM_HELP_URL = 'https://www.pbs.org/livestreamhelp'
const LIVESTREAM_HELP_DISPLAY = 'www.pbs.org/livestreamhelp'

type LocalizationErrorProps = {
  status?: LocalizationAuthStatus
  callSign?: string
  hasError?: boolean
}

const LocalizationError = ({
  status = LocalizationAuthStatus.WAITING,
  hasError = false,
  callSign = '',
}: LocalizationErrorProps) => {
  const navigate = useNavigate()
  const [readWholeScreen, setReadWholeScreen] = useState<boolean>(false)
  const [stations, setStations] = useState<Array<Station>>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const displayedStations = stations.slice(0, 3)
  const stationCount = stations.length
  const displayedStationCount = displayedStations.length
  const hasStations = stationCount > 0
  const preferredStationFocusKey =
    hasStations && displayedStationCount > 0
      ? `${STATION_BUTTON_FOCUS_PREFIX}0`
      : undefined
  const { ref, focusKey, hasFocusedChild } = useHybridFocusable({
    focusKey: COMPONENT_FOCUS_KEY,
    trackChildren: true,
    isFocusBoundary: true,
    focusable: true,
    preferredChildFocusKey: preferredStationFocusKey,
  })

  useKeyDown(KeyTypeEnum.Back, () => {
    tts.pushTextToRead('Going back to livestream')
    navigate('/livestream')
  })

  const getMessage = () => {
    const stationText = callSign ? callSign : ''
    if (status === LocalizationAuthStatus.UNAUTHORIZED && !hasError) {
      return `It looks like your devices may be outside of the ${stationText} broadcast coverage area.`
    }
    if (hasError || status === LocalizationAuthStatus.ERROR) {
      return "We're sorry, we couldn't access your device's location."
    }
    return `It looks like your devices may be outside of the ${stationText} broadcast coverage area.`
  }

  const MESSAGE_TEXT = getMessage()
  const MORE_MESSAGE_TEXT =
    'Select another station in your broadcast area to start watching live TV:'

  const getFullScreenReadout = () => {
    const sections = [MESSAGE_TEXT]

    if (hasStations) {
      sections.push(MORE_MESSAGE_TEXT)
    }

    sections.push(`For more information, visit: ${LIVESTREAM_HELP_DISPLAY}.`)

    return sections.join(' .... ')
  }

  useEffect(() => {
    store.dispatch({
      type: 'SET_SCHEDULES',
      payload: {
        channels: [],
      },
    })
  }, [])

  useEffect(() => {
    const fetchStations = async () => {
      try {
        setIsLoading(true)
        const localizedStations = await getLocalizedStations()
        const rawStations = localizedStations?.stations || []
        const fetchedStations: Array<Station> = rawStations.map((x: any) => ({
          callsign: x.callsign,
          cid: x.station_id,
          city: x.city,
          fullName: x.common_name_full,
          shortName: x.common_name_short,
        }))
        setStations(fetchedStations)
      } catch (err) {
        setStations([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchStations()
  }, [])

  useEffect(() => {
    if (!hasFocusedChild) {
      setReadWholeScreen(true)
    }
  }, [hasFocusedChild])

  useEffect(() => {
    if (!isLoading) {
      tts.addTextToRead(getFullScreenReadout())
    }
  }, [isLoading, MESSAGE_TEXT, hasStations])

  useEffect(() => {
    if (hasStations && displayedStationCount > 0 && preferredStationFocusKey) {
      const currentKey = getCurrentFocusKey()
      const hasStationFocused = currentKey?.startsWith(
        STATION_BUTTON_FOCUS_PREFIX
      )

      if (!hasStationFocused) {
        requestAnimationFrame(() => setFocus(preferredStationFocusKey))
      }
    }
  }, [hasStations, displayedStationCount, preferredStationFocusKey])

  const moveStationFocusByOffset = (offset: number) => {
    if (displayedStationCount <= 1 || offset === 0) return

    const currentKey = getCurrentFocusKey()
    const currentIndex = currentKey?.startsWith(STATION_BUTTON_FOCUS_PREFIX)
      ? Number(currentKey.replace(STATION_BUTTON_FOCUS_PREFIX, ''))
      : 0

    if (Number.isNaN(currentIndex)) return

    const targetIndex = currentIndex + offset
    if (targetIndex < 0 || targetIndex >= displayedStationCount) return

    setFocus(`${STATION_BUTTON_FOCUS_PREFIX}${targetIndex}`)
  }

  useKeyDown([KeyTypeEnum.Left, KeyTypeEnum.Up], () =>
    moveStationFocusByOffset(-1)
  )
  useKeyDown([KeyTypeEnum.Right, KeyTypeEnum.Down], () =>
    moveStationFocusByOffset(1)
  )

  useKeyDown(KeyTypeEnum.Enter, () => {
    const currentFocusKey = getCurrentFocusKey()
    if (!currentFocusKey?.startsWith(STATION_BUTTON_FOCUS_PREFIX)) return

    const index = Number(
      currentFocusKey.replace(STATION_BUTTON_FOCUS_PREFIX, '')
    )
    if (Number.isNaN(index)) return

    const station = displayedStations[index]
    if (station) {
      handleEnterPress(station)()
    }
  })

  const handleButtonFocus = (index: number, title: string) => {
    tts.stopRead()
    let buttonTts = `Button ${
      index + 1
    } of ${displayedStationCount} .... ${title}.`
    if (readWholeScreen) {
      buttonTts = `${getFullScreenReadout()} .... ${buttonTts}`
    }
    tts.addTextToRead(buttonTts)
    setReadWholeScreen(false)
  }

  const handleStationArrowPress = (direction: string) => {
    if (displayedStationCount <= 1) return true

    const currentFocusKey = getCurrentFocusKey()
    if (!currentFocusKey?.startsWith(STATION_BUTTON_FOCUS_PREFIX)) return true

    const currentIndex = Number(
      currentFocusKey.replace(STATION_BUTTON_FOCUS_PREFIX, '')
    )
    if (Number.isNaN(currentIndex)) return true

    const offset =
      direction === 'right' || direction === 'down'
        ? 1
        : direction === 'left' || direction === 'up'
        ? -1
        : 0

    if (offset === 0) return true

    const targetIndex = currentIndex + offset

    if (targetIndex < 0 || targetIndex >= displayedStationCount) return true

    setFocus(`${STATION_BUTTON_FOCUS_PREFIX}${targetIndex}`)
    return false
  }

  const handleEnterPress = (station: Station) => () => {
    tts.stopRead()
    loadStationSchedules(station.cid, station, true)
    tts.pushTextToRead(`Selected ${station.shortName} station.`)
    navigate('/livestream', {
      state: { fromLocalizationError: true },
    })
  }

  if (isLoading) {
    return (
      <div className="localization-error">
        <div className="localization-error__wrapper">
          <Spinner />
        </div>
      </div>
    )
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="localization-error">
        <div className="localization-error__wrapper">
          <div className="localization-error__content">
            <div className="localization-error__main">
              <div className="localization-error__text-content">
                <p className="localization-error__message">{MESSAGE_TEXT}</p>
                {hasStations && (
                  <div className="localization-error__stations">
                    <p className="localization-error__more-message">
                      {MORE_MESSAGE_TEXT}
                    </p>
                    <div className="localization-error__station-buttons">
                      {displayedStations.map((station, index) => (
                        <FocusableButtonTS
                          className="localization-error__button"
                          key={station.cid}
                          focusKey={`${STATION_BUTTON_FOCUS_KEY}_${index}`}
                          onArrowPress={handleStationArrowPress}
                          onEnterPress={handleEnterPress(station)}
                          onFocus={() =>
                            handleButtonFocus(index, station.callsign)
                          }
                          stealFocus={index === 0}
                        >
                          {station.shortName}
                        </FocusableButtonTS>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className="localization-error__footer">
            For more information, visit:{' '}
            <a href={LIVESTREAM_HELP_URL} rel="noreferrer" target="_blank">
              {LIVESTREAM_HELP_DISPLAY}
            </a>
          </p>
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default LocalizationError
